import { toast } from '@heroui/react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, Dropdown, Input, Label } from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { UserSelectDialog, type UserSelectItem } from '@/components/UserSelectDialog';
import {
  IconPlus as Plus,
  IconTrash as Trash2,
  IconDots as Ellipsis,
  IconFilePencil as FilePenLine,
  IconAlarmOff as AlarmClockOff,
  IconAlarm as AlarmClock,
  IconGift as Gift,
  IconRefresh as RefreshCw,
} from '@tabler/icons-react';
import { formatDateTime } from '@hs/shared/utils/formatTime';

type LotteryRule = {
  id?: string;
  prizeId: string;
  lotteryTime: string;
  winnerCount: number;
  allowRepeatWin?: boolean;
  lotteryMethod: 'AUTO' | 'MANUAL';
  executorId?: string | null;
  isScheduled?: boolean;
  isCompleted: boolean;
};

type NewRuleForm = {
  prizeId: string;
  lotteryTime: string | null;
  winnerCount: string;
  allowRepeatWin: boolean;
  lotteryMethod: 'AUTO' | 'MANUAL';
  executorId: string | null;
};

const LOTTERY_METHOD_OPTIONS = [
  { value: 'AUTO', label: '自动抽奖' },
  { value: 'MANUAL', label: '人工抽奖' },
] as const;

const DEFAULT_NEW_RULE: NewRuleForm = {
  prizeId: '',
  lotteryTime: null,
  winnerCount: '1',
  allowRepeatWin: false,
  lotteryMethod: 'AUTO',
  executorId: null,
};

/** `datetime-local` 用的本地字符串（含秒） */
function isoToDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function datetimeLocalToIso(v: string): string | null {
  if (!v.trim()) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const selectClass =
  'w-full min-w-0 rounded-md border border-divider bg-background px-2 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary';

interface LotteryRulesStepContentProps {
  activityId: string;
  activityTitle: string;
}

export function LotteryRulesStepContent({ activityId, activityTitle }: LotteryRulesStepContentProps) {
  const utils = trpc.useUtils();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [localRules, setLocalRules] = useState<LotteryRule[]>([]);
  const [newRule, setNewRule] = useState<NewRuleForm>(DEFAULT_NEW_RULE);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<LotteryRule>>({});
  const [userSelectOpen, setUserSelectOpen] = useState(false);
  const [userSelectFor, setUserSelectFor] = useState<'new' | 'edit' | null>(null);

  const { data: serverRules = [], isLoading: rulesLoading } = trpc.activityLotteryRules.listAll.useQuery(
    { activityId },
    { enabled: !!activityId }
  );
  const { data: prizes = [] } = trpc.activityPrizes.listAll.useQuery({ activityId }, { enabled: !!activityId });
  const createRule = trpc.activityLotteryRules.create.useMutation();
  const updateRule = trpc.activityLotteryRules.update.useMutation();
  const deleteRule = trpc.activityLotteryRules.delete.useMutation();
  const cancelSchedule = trpc.activityLotteryRules.cancelSchedule.useMutation();
  const restoreSchedule = trpc.activityLotteryRules.restoreSchedule.useMutation();
  const runDraw = trpc.activityLotteryRules.runDraw.useMutation();

  const refetchRules = () => {
    utils.activityLotteryRules.listAll.invalidate({ activityId });
    utils.activityLotteryRules.list.invalidate();
  };

  useEffect(() => {
    if (!rulesLoading) {
      const mapped: LotteryRule[] = serverRules.map((r) => ({
        id: String(r.id ?? ''),
        prizeId: String(r.prizeId ?? ''),
        lotteryTime:
          typeof r.lotteryTime === 'string'
            ? r.lotteryTime
            : r.lotteryTime != null && typeof r.lotteryTime === 'object'
              ? String((r.lotteryTime as Date).toISOString?.() ?? '')
              : '',
        winnerCount: typeof r.winnerCount === 'number' ? r.winnerCount : 1,
        allowRepeatWin: Boolean((r as { allowRepeatWin?: boolean }).allowRepeatWin),
        lotteryMethod: (r.lotteryMethod === 'MANUAL' ? 'MANUAL' : 'AUTO') as 'AUTO' | 'MANUAL',
        executorId: r.executorId != null ? String(r.executorId) : null,
        isScheduled: Boolean((r as { isScheduled?: boolean }).isScheduled),
        isCompleted: Boolean(r.isCompleted),
      }));
      setLocalRules(mapped);
    }
  }, [serverRules, rulesLoading]);

  const handleUserSelectConfirm = (selected: UserSelectItem[]) => {
    const user = selected[0];
    if (!user?.userId) return;
    if (userSelectFor === 'new') setNewRule((r) => ({ ...r, executorId: user.userId }));
    if (userSelectFor === 'edit') setEditForm((f) => ({ ...f, executorId: user.userId }));
    setUserSelectFor(null);
  };

  return (
    <div className="w-full max-w-6xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold">抽奖规则</h3>
          <p className="text-muted-foreground text-sm">活动：{activityTitle || '-'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onPress={() => setShowCreateForm(true)}
            isDisabled={showCreateForm}
          >
            <Plus className="mr-2 size-4" stroke={1.5} />
            添加规则
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onPress={() => {
              refetchRules();
              toast.success('已刷新');
            }}
          >
            <RefreshCw className="mr-2 size-4" stroke={1.5} />
            刷新
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-divider">
        <div className="overflow-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-background sticky top-0 z-10">
              <tr className="border-b border-divider">
                <th className="w-32 px-2 py-2 text-left font-medium">奖品</th>
                <th className="w-44 px-2 py-2 text-left font-medium">抽奖时间</th>
                <th className="w-24 px-2 py-2 text-left font-medium">获奖人数</th>
                <th className="w-28 px-2 py-2 text-left font-medium">允许重复中奖</th>
                <th className="w-24 px-2 py-2 text-left font-medium">抽奖方式</th>
                <th className="w-28 px-2 py-2 text-left font-medium">执行人</th>
                <th className="w-28 px-2 py-2 text-left font-medium">启用定时任务</th>
                <th className="w-20 px-2 py-2 text-left font-medium">已完成</th>
                <th className="w-24 px-2 py-2 text-left font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {showCreateForm && (
                <tr className="bg-muted/50">
                  <td className="px-2 py-2 align-top">
                    <select
                      className={selectClass}
                      value={newRule.prizeId}
                      onChange={(e) => setNewRule((r) => ({ ...r, prizeId: e.target.value }))}
                    >
                      <option value="">选择奖品</option>
                      {(prizes as { id?: string; name?: string }[]).map((p) => (
                        <option key={String(p.id)} value={String(p.id)}>
                          {p.name ?? String(p.id)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-2 align-top">
                    <input
                      type="datetime-local"
                      step={1}
                      className={selectClass}
                      value={isoToDatetimeLocalValue(newRule.lotteryTime ?? '')}
                      onChange={(e) =>
                        setNewRule((r) => ({
                          ...r,
                          lotteryTime: datetimeLocalToIso(e.target.value),
                        }))
                      }
                    />
                  </td>
                  <td className="px-2 py-2 align-top">
                    <Input
                      type="number"
                      min={1}
                      value={newRule.winnerCount}
                      onChange={(e) => setNewRule((r) => ({ ...r, winnerCount: e.target.value }))}
                    />
                  </td>
                  <td className="px-2 py-2 align-top">
                    <select
                      className={selectClass}
                      value={newRule.allowRepeatWin ? 'true' : 'false'}
                      onChange={(e) =>
                        setNewRule((r) => ({ ...r, allowRepeatWin: e.target.value === 'true' }))
                      }
                    >
                      <option value="false">否</option>
                      <option value="true">是</option>
                    </select>
                  </td>
                  <td className="px-2 py-2 align-top">
                    <select
                      className={selectClass}
                      value={newRule.lotteryMethod}
                      onChange={(e) => {
                        const method = e.target.value === 'MANUAL' ? 'MANUAL' : 'AUTO';
                        setNewRule((r) => ({
                          ...r,
                          lotteryMethod: method,
                          executorId: method === 'AUTO' ? null : r.executorId,
                        }));
                      }}
                    >
                      {LOTTERY_METHOD_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-2 align-top">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="w-full"
                      isDisabled={newRule.lotteryMethod === 'AUTO'}
                      onPress={() => {
                        setUserSelectFor('new');
                        setUserSelectOpen(true);
                      }}
                    >
                      {newRule.lotteryMethod === 'MANUAL' && newRule.executorId ? '已选' : '选择用户'}
                    </Button>
                  </td>
                  <td className="text-muted-foreground px-2 py-2">—</td>
                  <td className="text-muted-foreground px-2 py-2">—</td>
                  <td className="px-2 py-2 align-top">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onPress={async () => {
                          const w = parseInt(newRule.winnerCount, 10);
                          if (!newRule.prizeId) return toast.danger('请选择奖品');
                          if (!newRule.lotteryTime) return toast.danger('请选择抽奖时间');
                          if (Number.isNaN(w) || w < 1) return toast.danger('请输入有效获奖人数');
                          if (newRule.lotteryMethod === 'MANUAL' && !newRule.executorId)
                            return toast.danger('人工抽奖时请选择执行人');
                          await createRule.mutateAsync({
                            activityId,
                            prizeId: newRule.prizeId,
                            lotteryTime: new Date(newRule.lotteryTime).toISOString(),
                            winnerCount: w,
                            allowRepeatWin: newRule.allowRepeatWin,
                            lotteryMethod: newRule.lotteryMethod,
                            executorId:
                              newRule.lotteryMethod === 'MANUAL'
                                ? newRule.executorId ?? undefined
                                : undefined,
                            isCompleted: false,
                          });
                          toast.success('抽奖规则已添加');
                          setShowCreateForm(false);
                          setNewRule(DEFAULT_NEW_RULE);
                          refetchRules();
                        }}
                      >
                        保存
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onPress={() => {
                          setShowCreateForm(false);
                          setNewRule(DEFAULT_NEW_RULE);
                        }}
                      >
                        取消
                      </Button>
                    </div>
                  </td>
                </tr>
              )}

              {localRules.map((rule, idx) =>
                editingIndex === idx ? (
                  <tr key={rule.id ?? `edit-${idx}`} className="bg-muted/50">
                    <td className="px-2 py-2 align-top">
                      <select
                        className={selectClass}
                        value={editForm.prizeId ?? ''}
                        onChange={(e) => setEditForm((f) => ({ ...f, prizeId: e.target.value }))}
                      >
                        <option value="">选择奖品</option>
                        {(prizes as { id?: string; name?: string }[]).map((p) => (
                          <option key={String(p.id)} value={String(p.id)}>
                            {p.name ?? String(p.id)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-2 align-top">
                      <input
                        type="datetime-local"
                        step={1}
                        className={selectClass}
                        value={isoToDatetimeLocalValue(
                          typeof editForm.lotteryTime === 'string' ? editForm.lotteryTime : ''
                        )}
                        onChange={(e) =>
                          setEditForm((f) => ({
                            ...f,
                            lotteryTime: datetimeLocalToIso(e.target.value) ?? undefined,
                          }))
                        }
                      />
                    </td>
                    <td className="px-2 py-2 align-top">
                      <Input
                        type="number"
                        min={1}
                        value={editForm.winnerCount != null ? String(editForm.winnerCount) : ''}
                        onChange={(e) =>
                          setEditForm((f) => ({
                            ...f,
                            winnerCount: parseInt(e.target.value, 10) || 0,
                          }))
                        }
                      />
                    </td>
                    <td className="px-2 py-2 align-top">
                      <select
                        className={selectClass}
                        value={editForm.allowRepeatWin ? 'true' : 'false'}
                        onChange={(e) =>
                          setEditForm((f) => ({ ...f, allowRepeatWin: e.target.value === 'true' }))
                        }
                      >
                        <option value="false">否</option>
                        <option value="true">是</option>
                      </select>
                    </td>
                    <td className="px-2 py-2 align-top">
                      <select
                        className={selectClass}
                        value={editForm.lotteryMethod ?? 'AUTO'}
                        onChange={(e) => {
                          const method = e.target.value === 'MANUAL' ? 'MANUAL' : 'AUTO';
                          setEditForm((f) => ({
                            ...f,
                            lotteryMethod: method,
                            executorId: method === 'AUTO' ? undefined : f.executorId,
                          }));
                        }}
                      >
                        {LOTTERY_METHOD_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-2 align-top">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="w-full"
                        isDisabled={editForm.lotteryMethod === 'AUTO'}
                        onPress={() => {
                          setUserSelectFor('edit');
                          setUserSelectOpen(true);
                        }}
                      >
                        {editForm.executorId ? '已选' : '选择用户'}
                      </Button>
                    </td>
                    <td className="px-2 py-2">{rule.isScheduled ? '已启用' : '未启用'}</td>
                    <td className="px-2 py-2">{rule.isCompleted ? '是' : '否'}</td>
                    <td className="px-2 py-2 align-top">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onPress={async () => {
                            if (!rule.id) return;
                            const w = editForm.winnerCount ?? 0;
                            if (w < 1) return toast.danger('获奖人数至少为 1');
                            const lt = editForm.lotteryTime;
                            if (!lt || typeof lt !== 'string') return toast.danger('请选择抽奖时间');
                            await updateRule.mutateAsync({
                              id: rule.id,
                              data: {
                                prizeId: editForm.prizeId,
                                lotteryTime: new Date(lt).toISOString(),
                                winnerCount: w,
                                allowRepeatWin: editForm.allowRepeatWin,
                                lotteryMethod: editForm.lotteryMethod,
                                executorId: editForm.executorId ?? null,
                              },
                            });
                            toast.success('规则已更新');
                            setEditingIndex(null);
                            setEditForm({});
                            refetchRules();
                          }}
                        >
                          保存
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onPress={() => {
                            setEditingIndex(null);
                            setEditForm({});
                          }}
                        >
                          取消
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={rule.id ?? `new-${idx}`} className="hover:bg-muted/50">
                    <td className="px-2 py-2 font-medium">
                      {(prizes as { id?: string; name?: string }[]).find(
                        (x) => String(x.id) === String(rule.prizeId)
                      )?.name ?? rule.prizeId}
                    </td>
                    <td className="px-2 py-2">{formatDateTime(rule.lotteryTime) || rule.lotteryTime}</td>
                    <td className="px-2 py-2">{rule.winnerCount}</td>
                    <td className="px-2 py-2">{rule.allowRepeatWin ? '是' : '否'}</td>
                    <td className="px-2 py-2">
                      {LOTTERY_METHOD_OPTIONS.find((o) => o.value === rule.lotteryMethod)?.label ??
                        rule.lotteryMethod}
                    </td>
                    <td className="px-2 py-2">
                      {rule.lotteryMethod === 'MANUAL' && rule.executorId ? rule.executorId : '—'}
                    </td>
                    <td className="px-2 py-2">{rule.isScheduled ? '已启用' : '未启用'}</td>
                    <td className="px-2 py-2">{rule.isCompleted ? '是' : '否'}</td>
                    <td className="px-2 py-2">
                      <Dropdown>
                        <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
                          <Ellipsis className="size-4" />
                        </Button>
                        <Dropdown.Popover className="min-w-[180px]" placement="bottom end">
                          <Dropdown.Menu>
                            <Dropdown.Item
                              id="edit"
                              isDisabled={rule.isCompleted}
                              textValue="编辑"
                              onAction={() => {
                                if (!rule.id || rule.isCompleted) return;
                                setEditingIndex(idx);
                                setEditForm({
                                  prizeId: rule.prizeId,
                                  lotteryTime: rule.lotteryTime,
                                  winnerCount: rule.winnerCount,
                                  allowRepeatWin: rule.allowRepeatWin,
                                  lotteryMethod: rule.lotteryMethod,
                                  executorId: rule.executorId ?? undefined,
                                });
                              }}
                            >
                              <FilePenLine className="mr-2 size-4" />
                              <Label>编辑</Label>
                            </Dropdown.Item>
                            <Dropdown.Item
                              id="delete"
                              isDisabled={rule.isCompleted}
                              textValue="删除"
                              variant="danger"
                              onAction={async () => {
                                if (!rule.id || rule.isCompleted) return;
                                await deleteRule.mutateAsync({ id: rule.id });
                                toast.success('已删除');
                                refetchRules();
                              }}
                            >
                              <Trash2 className="mr-2 size-4" />
                              <Label>删除</Label>
                            </Dropdown.Item>
                            {rule.lotteryMethod === 'MANUAL' && rule.id ? (
                              <Dropdown.Item
                                id="run"
                                isDisabled={rule.isCompleted}
                                textValue="执行抽奖"
                                onAction={() => {
                                  if (rule.id && !rule.isCompleted) runDraw.mutate({ id: rule.id });
                                }}
                              >
                                <Gift className="mr-2 size-4" />
                                <Label>执行抽奖</Label>
                              </Dropdown.Item>
                            ) : null}
                            {rule.lotteryMethod === 'AUTO' && rule.id && !rule.isCompleted && rule.isScheduled ? (
                              <Dropdown.Item
                                id="cancel-schedule"
                                textValue="取消定时任务"
                                onAction={async () => {
                                  await cancelSchedule.mutateAsync({ id: rule.id! });
                                  refetchRules();
                                }}
                              >
                                <AlarmClockOff className="mr-2 size-4" />
                                <Label>取消定时任务</Label>
                              </Dropdown.Item>
                            ) : null}
                            {rule.lotteryMethod === 'AUTO' &&
                            rule.id &&
                            !rule.isCompleted &&
                            !rule.isScheduled ? (
                              <Dropdown.Item
                                id="restore-schedule"
                                textValue="恢复定时任务"
                                onAction={async () => {
                                  await restoreSchedule.mutateAsync({ id: rule.id! });
                                  refetchRules();
                                }}
                              >
                                <AlarmClock className="mr-2 size-4" />
                                <Label>恢复定时任务</Label>
                              </Dropdown.Item>
                            ) : null}
                          </Dropdown.Menu>
                        </Dropdown.Popover>
                      </Dropdown>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      <UserSelectDialog
        open={userSelectOpen}
        onOpenChange={(next) => {
          setUserSelectOpen(next);
          if (!next) setUserSelectFor(null);
        }}
        onConfirm={handleUserSelectConfirm}
      />
    </div>
  );
}
